import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { RETAILER_ID } from 'src/app/config';
import { ReportStatus, UserAction } from 'src/app/enum';
import { AuthenticationService } from 'src/app/_services';
import {
  ISalesReportListItem,
  ReportsService
} from 'src/app/_services/reports.service';

@Component({
  selector: 'reports-list-card',
  templateUrl: './reports-list.card.html',
  styleUrls: ['./reports-list.card.scss']
})
export class ReportsListCardComponent implements OnInit, OnDestroy {
  public illustrationSrc = '/assets/img/reports/sales-stock-illustration.png';
  public isOpening = false;

  public readonly selver = {
    id: RETAILER_ID.SELVER,
    name: 'Selver AS',
    logoSrc: '/assets/img/reports/selver-logo.png'
  };

  private _reports$: Observable<ISalesReportListItem[]>;
  private _preloadSub: Subscription;
  private _openSub: Subscription;

  constructor(
    private _router: Router,
    private _reportsService: ReportsService,
    private _authService: AuthenticationService
  ) {}

  public ngOnInit() {
    this._reports$ = this._reportsService.listSalesReports().pipe(
      map((response) => response?.items || []),
      catchError(() => of([])),
      shareReplay(1)
    );
    this._preloadSub = this._reports$.subscribe();
  }

  public ngOnDestroy() {
    this._preloadSub?.unsubscribe();
    this._openSub?.unsubscribe();
  }

  public openSelverReport() {
    this.openProviderReport(this.selver.id);
  }

  private openProviderReport(providerId: string) {
    if (this.isOpening) {
      return;
    }

    this.isOpening = true;
    this._openSub?.unsubscribe();
    this._openSub = this._reports$.pipe(take(1)).subscribe((reports) => {
      const activeReport = this.findActiveReport(providerId, reports);

      if (activeReport?.id) {
        this._router.navigate(['/reports/sales', providerId, activeReport.id]);
        return;
      }

      if (this._authService.hasRight([UserAction.CreateReports])) {
        this._router.navigate(['/reports/sales', providerId]);
        return;
      }

      this.isOpening = false;
    });
  }

  private findActiveReport(
    providerId: string,
    reports: ISalesReportListItem[]
  ): ISalesReportListItem | undefined {
    const provider = providerId.toLowerCase();

    return reports.find(
      (report) =>
        report.providerId?.toLowerCase() === provider &&
        report.status !== ReportStatus.Expired &&
        this.canViewReport(report)
    );
  }

  private canViewReport(report: ISalesReportListItem): boolean {
    return this._authService.isUserOwner(report.createdById)
      ? this._authService.hasRight([UserAction.ViewReports])
      : this._authService.hasRight([UserAction.ViewOthersReports]);
  }
}
